import { useOldDatasetHref } from "@/features/datasets/useDatasetsAccess";
import { useRepositories } from "@/features/repos/hooks/useRepositories";
import { cn } from "@/utils/cn";
import { Link, useMatch } from "react-router-dom";

const REPO_LINK =
  "font-ui block truncate text-zinc-500 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:text-zinc-900";

const DATASET_LINK =
  "font-ui block truncate pl-3 text-xs leading-5 text-zinc-400 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:text-zinc-900";

const HEADING_ID = "rail-repositories";

/** Left rail of the Repositories page: every repository, with links to its datasets. */
export const RepositoriesSidebarSection = ({ onNavigate }: { onNavigate?: () => void }) => {
  const activeRepoId = useMatch({ path: "/repos/:repoId", end: false })?.params.repoId;
  const { list, datasetsByRepo, isLoading, isError } = useRepositories();
  const datasetHref = useOldDatasetHref();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <p id={HEADING_ID} className="font-ui shrink-0 px-5 pb-2 text-zinc-400">
        Repositories
      </p>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 text-sm leading-6">
        {isLoading ? (
          <p className="font-ui text-zinc-400">Loading…</p>
        ) : isError ? (
          <p role="alert" className="font-ui text-zinc-500">
            Could not load the repositories.
          </p>
        ) : list.length === 0 ? (
          <p className="font-serif text-zinc-400">
            No repositories yet. Each dataset keeps its evidence in one.
          </p>
        ) : (
          <ul aria-labelledby={HEADING_ID} className="space-y-1">
            {list.map((repo) => {
              const active = repo.id === activeRepoId;
              return (
                <li key={repo.id}>
                  <Link
                    to={`/repos/${encodeURIComponent(repo.id)}`}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    title={repo.name}
                    className={cn(REPO_LINK, active && "font-medium text-zinc-900")}
                  >
                    {repo.name}
                  </Link>
                  {(datasetsByRepo.get(repo.id) ?? []).map((dataset) => (
                    <Link
                      key={dataset.id}
                      to={datasetHref(dataset.id)}
                      onClick={onNavigate}
                      title={`Open the dataset “${dataset.name}”`}
                      className={DATASET_LINK}
                    >
                      Dataset: {dataset.name}
                    </Link>
                  ))}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
